import { useState } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import ReviewMasonryItem from "./review-masonry-item";
import type { Review, ReviewContentType } from "../types";
import { DeleteConfirmationModal } from "./delete-confirmation-modal";
import { getMuxThumbnailUrl } from "../utils/mux-urls";

export interface ReviewMasonryProps {
	reviews: Review[];
	onDelete?: (id: string) => void;
	onEdit?: (id: string) => void;
	onApprove?: (id: string) => void;
	onUnapprove?: (id: string) => void;
	onClick?: (id: string) => void;
	className?: string;
	statusChangingReviewId?: string | null;
	isDemoMode?: boolean;
}

export default function ReviewMasonry({ reviews, onDelete, onEdit, onApprove, onUnapprove, onClick, className = "", statusChangingReviewId, isDemoMode = false }: ReviewMasonryProps) {
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedReview, setSelectedReview] = useState<Review | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const handleDeleteClick = (review: Review) => {
		setSelectedReview(review);
		setDeleteModalOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedReview || !onDelete) return;

		setIsDeleting(true);
		try {
			await onDelete(selectedReview.id);
			setDeleteModalOpen(false);
			setSelectedReview(null);
		} catch (error) {
			console.error("Failed to delete review:", error);
			alert("Failed to delete review. Please try again.");
		} finally {
			setIsDeleting(false);
		}
	};

	// Helper to get video info from review
	const getVideoInfo = (review: Review) => {
		// If video is still processing, return processing state
		if (review.video_status === "processing") {
			return { playbackId: null, thumbnailUrl: null, isProcessing: true };
		}

		// Get playback ID and generate thumbnail URL
		const playbackId = review.mux_playback_id || null;
		const thumbnailUrl = review.video_thumbnail_url || (playbackId ? getMuxThumbnailUrl(playbackId) : null);

		return { playbackId, thumbnailUrl, isProcessing: false };
	};

	return (
		<>
			<div className={className}>
				<ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 768: 2, 1024: 3 }}>
					<Masonry gutter="1rem">
						{reviews.map((review) => {
							const { playbackId, thumbnailUrl, isProcessing } = getVideoInfo(review);
							return (
								<ReviewMasonryItem
									key={review.id}
									rating={review.rating}
									type={review.type}
									testimonial={review.content_text}
									date={formatDate(review.date)}
									source={review.source}
									tags={review.tags}
									status={review.status as "draft" | "pending" | "approved" | "unapproved"}
									contentType={review.content_type as ReviewContentType}
									audioUrl={review.audio_url}
									audioDuration={review.audio_duration}
									muxPlaybackId={playbackId}
									videoThumbnailUrl={thumbnailUrl}
									videoDuration={review.video_duration}
									videoStatus={review.video_status}
									isVideoProcessing={isProcessing}
									customerName={review.customer_name}
									customerHeadline={review.customer_headline}
									customerJobTitle={review.customer_job_title}
									customerAvatarUrl={review.customer_avatar_url}
									companyName={review.customer_company_name}
									companyLogoUrl={review.customer_company_logo_url}
									onDelete={!isDemoMode && onDelete ? () => handleDeleteClick(review) : undefined}
									onEdit={!isDemoMode && onEdit ? () => onEdit(review.id) : undefined}
									onApprove={onApprove ? () => onApprove(review.id) : undefined}
									onUnapprove={!isDemoMode && onUnapprove ? () => onUnapprove(review.id) : undefined}
									onClick={!isDemoMode && onClick ? () => onClick(review.id) : undefined}
									isDemoMode={isDemoMode}
									isStatusChanging={statusChangingReviewId === review.id}
								/>
							);
						})}
					</Masonry>
				</ResponsiveMasonry>
			</div>

			<DeleteConfirmationModal
				open={deleteModalOpen}
				title="Delete Review"
				description="Are you sure you want to delete this review? This action cannot be undone."
				isLoading={isDeleting}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
			/>
		</>
	);
}
